import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as SearchIcon } from 'assets/svg/search.svg';
import { debounce, get } from 'lodash';
import { cache } from 'graph_ql/index';
import { useQuery } from '@apollo/client';
import { AlgoliaInput, AlgoliaResponse } from 'graph_ql/types/types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Spinner } from 'react-bootstrap';
import { searchConsumerStyles } from './searchConsumerStyles';
import { IAlgoliaStore } from '@gql/store/types';
import { RootState, useAppDispatch, useAppSelector } from '../../../redux/store';
import { setSelectedStore } from 'redux/create-style/create-style.slice';
import { GET_STORES } from 'graph_ql/store/queries';
import { Radio } from '@mui/material';

const SearchStore = (props) => {
    const { setSelectedConsumer } = props;
    const { selectedStore } = useAppSelector((state: RootState) => state.createStyle);
    const dispatch = useAppDispatch();
    const [selectedOption, setSelectedOption] = useState<any>(selectedStore);
    const [searchTerm, setSearchTerm] = useState('');

    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isShowOptions, setIsShowOptions] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const getMoreTimeout = useRef<any>(null);

    const getStoreVariables = useMemo(() => {
        cache.modify({
            fields: {
                algoliaGetStores(existingTaskRefs, { readField }) {
                    return {};
                },
            },
        });
        return {
            query: searchTerm,
            filters: `approved:true AND uninstalled:false`,
            page: 0,
            hit: 20,
        };
        // eslint-disable-next-line
    }, [searchTerm]);

    const { fetchMore: fetchStores } = useQuery<AlgoliaResponse<IAlgoliaStore, 'algoliaGetStores'>, AlgoliaInput>(
        GET_STORES,
        {
            variables: getStoreVariables,
            fetchPolicy: 'cache-first',
            onCompleted(res) {
                if (!res) return;
                onGetStores(res, false);
            },
        },
    );

    const onGetStores = (res, fetch) => {
        if (get(res, ['algoliaGetStores', 'hits'], []).length) {
            const data = res.algoliaGetStores.hits.map((item) => {
                const url = new URL(item.url);
                return {
                    id: item.id,
                    name: item.name,
                    logoImageUrl: item.logoImageUrl,
                    shopifyStorefrontToken: item.shopifyStorefrontToken,
                    affiliateLink: item.affiliateLink,
                    url: item.url,
                    domain: url.hostname,
                    platform: item.platform,
                    useTaffiItems: item.useTaffiItems,
                };
            });
            if (res.algoliaGetStores.page >= res.algoliaGetStores.nbPages - 1) setHasMore(false);
            else {
                setHasMore(true);
                setCurrentPage(res?.algoliaGetStores?.page);
            }
            if (fetch) setSuggestions((prev) => [...(prev || []), ...(data || [])]);
            else setSuggestions(data);
        } else {
            setHasMore(false);
        }
    };

    useEffect(() => {
        setCurrentPage(0);
        if (selectedStore) {
            setSearchTerm(selectedStore.name);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchMoreData = async (page) => {
        const data = await fetchStores({
            variables: {
                page: page,
                hit: 20,
            },
        });
        onGetStores(data?.data, true);
    };

    useEffect(() => {
        if (renderItemSuggestions(suggestions).length === 0 && hasMore) {
            setCurrentPage((prev) => prev + 1);
            if (getMoreTimeout.current) {
                clearTimeout(getMoreTimeout.current);
            }
            getMoreTimeout.current = setTimeout(() => {
                fetchMoreData(currentPage + 1);
            }, 2500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, selectedOption]);

    const onSelectOption = (option) => {
        // Deselect current option
        if (selectedOption && option.id === selectedOption.id) {
            handleDeleteItem();
            return;
        }

        setSelectedOption(option);
        dispatch(setSelectedStore(option));
        if (setSelectedConsumer) {
            setSelectedConsumer(null);
        }
    };

    const handleDeleteItem = () => {
        setSelectedOption(null);
        dispatch(setSelectedStore(null));
    };

    const renderItemSuggestions = (suggestions: any[]) => {
        return suggestions.filter((item) => item.id !== selectedOption?.id);
    };
    const classes = searchConsumerStyles({
        isShowOptions,
        suggestionsHeight: renderItemSuggestions(suggestions).length,
    });

    const debounceSearch = useRef(debounce((value) => {}, 300)).current;

    const handleChangeSearchTerm = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        debounceSearch(value);
    };

    return (
        <div className={classes.sortWrap}>
            <div className={classes.searchBox} onClick={() => setIsShowOptions((prev) => !prev)}>
                <SearchIcon />
                <div style={{ width: '100%' }}>
                    <input value={searchTerm} onChange={handleChangeSearchTerm} className={classes.input} />
                </div>
            </div>
            <div id={'scrollableDiv'} className={classes.selectedOptions}>
                <InfiniteScroll
                    dataLength={suggestions.length}
                    next={() => {
                        setCurrentPage((prev) => prev + 1);
                        if (getMoreTimeout.current) {
                            clearTimeout(getMoreTimeout.current);
                        }
                        getMoreTimeout.current = setTimeout(() => {
                            fetchMoreData(currentPage + 1);
                        }, 1500);
                    }}
                    hasMore={hasMore}
                    loader={
                        <div className={classes.noSuggestions}>
                            <Spinner animation="border" style={{ width: 20, height: 20 }} />
                        </div>
                    }
                    scrollableTarget="scrollableDiv"
                >
                    {suggestions.map((item: any) => (
                        <div
                            key={item.id}
                            className={classes.optionItem}
                            onClick={() => {
                                onSelectOption(item);
                            }}
                        >
                            <img src={item.logoImageUrl} alt="" className={classes.suggestionAvatar} />
                            <div className={classes.selectedOptionTitle}>{item.name}</div>
                            <div>
                                <Radio
                                    color="default"
                                    name="address"
                                    value={Boolean(selectedOption && item.id === selectedOption.id)}
                                    checked={Boolean(selectedOption && item.id === selectedOption.id)}
                                    classes={{ root: classes.radio, checked: classes.checked }}
                                    onClick={() => {
                                        onSelectOption(item);
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </InfiniteScroll>
            </div>
        </div>
    );
};

export default SearchStore;
